import {UploadStrategy, UploadStrategyConfig} from './upload-strategy';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import axios, {AxiosProgressEvent} from 'axios';
import {FileEntry} from '../../file-entry';
import {getAxiosErrorMessage} from '@common/http/get-axios-error-message';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

export class UserApiUpload implements UploadStrategy {
  private abortController: AbortController;
  private resolvedApiUrl?: string;
  private resolvedApiToken?: string;

  constructor(
    private file: UploadedFile,
    private config: UploadStrategyConfig,
  ) {
    this.abortController = new AbortController();
  }

  async start() {
    console.log('UserApiUpload.start() called');
    // Step 1: Upload file to User API
    const userApiResponse = await this.uploadToUserApi();
    if (!userApiResponse) {
      console.log('uploadToUserApi returned null, stopping');
      return;
    }

    console.log('User API upload successful, creating file entry locally');
    // Step 2: Create file entry in local database
    const response = await this.createFileEntry(userApiResponse);
    if (response?.fileEntry) {
      console.log('File entry created successfully');
      this.config.onSuccess?.(response.fileEntry, this.file);
    } else if (!this.abortController.signal.aborted) {
      console.log('File entry creation failed');
      this.config.onError?.(null, this.file);
    }
  }

  abort() {
    this.abortController.abort();
    return Promise.resolve();
  }

  private async uploadToUserApi(): Promise<{
    fileEntry: any;
    hash: string;
  } | null> {
    try {
      const credentials = this.getApiCredentials();
      if (!credentials) {
        return null;
      }
      const {apiUrl, apiToken} = credentials;

      if (!apiUrl || !apiToken) {
        const appName = import.meta.env.VITE_APP_NAME || 'Application';
        const errorMsg = `${appName} credentials not configured. URL: ${apiUrl || 'missing'}, Token: ${apiToken ? 'present' : 'missing'}`;
        console.error('uploadToUserApi: Credentials missing', errorMsg);
        this.config.onError?.(errorMsg, this.file);
        return null;
      }

      let userFolderId = this.parseFolderId(this.config.metadata?.userApiParentId);
      if (!userFolderId) {
        userFolderId = await this.resolveUserFolderId();
      }

      const targetParentId = await this.resolveTargetParentId(userFolderId);

      const formData = new FormData();
      formData.append('file', this.file.native);

      if (targetParentId) {
        formData.append('parentId', String(targetParentId));
      }

      const response = await axios.post(`${apiUrl}/api/v1/uploads`, formData, {
        signal: this.abortController.signal,
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Accept': 'application/json',
        },
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.event.lengthComputable) {
            this.config.onProgress?.({
              bytesUploaded: e.loaded,
              bytesTotal: e.total || 0,
            });
          }
        },
      });

      return {
        fileEntry: response.data.fileEntry,
        hash: response.data.fileEntry.hash,
      };
    } catch (err: any) {
      if (err.code !== 'ERR_CANCELED') {
        console.error('uploadToUserApi: Error', err);
        this.config.onError?.(getAxiosErrorMessage(err), this.file);
      }
      return null;
    }
  }

  private getApiCredentials(): {apiUrl: string; apiToken: string} | null {
    if (this.resolvedApiUrl && this.resolvedApiToken) {
      return {
        apiUrl: this.resolvedApiUrl,
        apiToken: this.resolvedApiToken,
      };
    }

    const bootstrapData = getBootstrapData();
    const settings = bootstrapData.settings;
    const apiUrl = (settings?.uploads as any)?.user_api_url;
    const apiToken = (settings?.uploads as any)?.user_api_token;

    if (!apiUrl || !apiToken) {
      const appName = import.meta.env.VITE_APP_NAME || 'Application';
      this.config.onError?.(
        `${appName} credentials not configured. URL: ${apiUrl || 'missing'}, Token: ${apiToken ? 'present' : 'missing'}`,
        this.file,
      );
      return null;
    }

    this.resolvedApiUrl = apiUrl;
    this.resolvedApiToken = apiToken;

    return {
      apiUrl,
      apiToken,
    };
  }

  private parseFolderId(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '' || value === 'null') {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  }

  private async resolveUserFolderId(): Promise<number | undefined> {
    try {
      const folderResponse = await apiClient.get('user-api/folder-id');
      const folderId = this.parseFolderId(folderResponse?.data?.folderId);
      if (folderId) {
        return folderId;
      }

      // retry once for transient null folder responses
      const retryResponse = await apiClient.get('user-api/folder-id');
      return this.parseFolderId(retryResponse?.data?.folderId);
    } catch {
      return undefined;
    }
  }

  private getRelativeFolderSegments(): string[] {
    const relativePath = this.file.relativePath || '';
    if (!relativePath.includes('/')) return [];

    const fileName = this.file.name;
    const directoryPath = relativePath.endsWith(`/${fileName}`)
      ? relativePath.slice(0, -1 * (fileName.length + 1))
      : relativePath.split('/').slice(0, -1).join('/');

    return directoryPath
      .split('/')
      .map(segment => segment.trim())
      .filter(Boolean);
  }

  private async resolveTargetParentId(
    userFolderId?: number,
  ): Promise<number | undefined> {
    const segments = this.getRelativeFolderSegments();
    if (!segments.length) {
      return userFolderId;
    }

    const credentials = this.getApiCredentials();
    if (!credentials) {
      return userFolderId;
    }

    let currentParentId = userFolderId;
    for (const segment of segments) {
      const folderId = await this.findOrCreateFolder(segment, currentParentId);
      if (!folderId) {
        return currentParentId;
      }
      currentParentId = folderId;
    }

    return currentParentId;
  }

  private async findOrCreateFolder(
    folderName: string,
    parentId?: number,
  ): Promise<number | undefined> {
    const credentials = this.getApiCredentials();
    if (!credentials) {
      return undefined;
    }
    const {apiUrl, apiToken} = credentials;

    const queryParams: Record<string, string | number> = {
      query: folderName,
      type: 'folder',
      perPage: 50,
    };
    if (parentId) {
      queryParams.parentIds = parentId;
    }

    try {
      const searchResponse = await axios.get(`${apiUrl}/api/v1/drive/file-entries`, {
        signal: this.abortController.signal,
        headers: {
          Authorization: `Bearer ${apiToken}`,
          Accept: 'application/json',
        },
        params: queryParams,
      });

      const existing = (searchResponse.data?.pagination?.data || []).find(
        (entry: any) =>
          entry?.type === 'folder' &&
          entry?.name === folderName &&
          (!parentId || Number(entry?.parent_id) === parentId),
      );
      const existingId = this.parseFolderId(existing?.id);
      if (existingId) {
        return existingId;
      }
    } catch {
      // continue with creation fallback
    }

    const payload: Record<string, string | number> = {name: folderName};
    if (parentId) {
      payload.parentId = parentId;
    }

    try {
      const createResponse = await axios.post(`${apiUrl}/api/v1/folders`, payload, {
        signal: this.abortController.signal,
        headers: {
          Authorization: `Bearer ${apiToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      return (
        this.parseFolderId(createResponse.data?.folder?.id) ||
        this.parseFolderId(createResponse.data?.fileEntry?.id) ||
        this.parseFolderId(createResponse.data?.data?.folder?.id) ||
        this.parseFolderId(createResponse.data?.id)
      );
    } catch {
      return undefined;
    }
  }

  private async createFileEntry(userApiResponse: {
    fileEntry: any;
    hash: string;
  }) {
    try {
      const localParentId = this.parseFolderId(this.config.metadata?.parentId);

      console.log('createFileEntry: Creating entry with data', {
        filename: this.file.name,
        size: this.file.size,
        remote_id: userApiResponse.fileEntry.id, // Store remote ID from spacebyte.in
        disk: 'user_api', // Specify disk driver to use for file operations
      });
      
      const fallbackName = String(userApiResponse.fileEntry.id);
      const remoteHash = userApiResponse.hash || fallbackName;

      return await apiClient
        .post('user-api/entries', {
          ...this.config.metadata,
          parentId: localParentId ?? null,
          clientMime: this.file.mime,
          clientName: this.file.name,
          // prefer remote hash; fallback to remote_id so file_name is always unique
          filename: remoteHash,
          size: this.file.size,
          clientExtension: this.file.extension,
          disk: 'user_api',
          remote_id: userApiResponse.fileEntry.id,
          user_api_hash: remoteHash,
        })
        .then(r => {
          console.log('createFileEntry: Success', r.data);
          return r.data as {fileEntry: FileEntry};
        });
    } catch (err: any) {
      if (err.code !== 'ERR_CANCELED') {
        console.error('createFileEntry: Error', err);
        this.config.onError?.(getAxiosErrorMessage(err), this.file);
      }
      return null;
    }
  }

  static async create(
    file: UploadedFile,
    config: UploadStrategyConfig,
  ): Promise<UserApiUpload> {
    return new UserApiUpload(file, config);
  }
}
